import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Moon, ArrowLeft, Clock, Play, Pause, ChevronRight, Heart, Calendar, Volume2, SkipForward, SkipBack, Loader2, Star, Crown, Lock } from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const MeditationsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [meditations, setMeditations] = useState([]);
  const [dailyMeditation, setDailyMeditation] = useState(null);
  const [selectedMeditation, setSelectedMeditation] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(searchParams.get('favorites') === 'true');
  const [isPremium, setIsPremium] = useState(false);
  const [specialPlaying, setSpecialPlaying] = useState(null);
  const specialAudioRef = useRef(null);
  
  // Audio guide states
  const [isAudioGuide, setIsAudioGuide] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const audioRef = useRef(null);
  const currentStepRef = useRef(0);

  useEffect(() => {
    fetchMeditations();
    fetchFavorites();
    fetchPremiumStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (specialAudioRef.current) {
        specialAudioRef.current.pause();
        specialAudioRef.current = null;
      }
    };
  }, []);

  const fetchFavorites = async () => {
    try {
      const res = await axios.get(`${API}/favorites/meditations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(res.data.favorites || []);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    }
  };

  const fetchPremiumStatus = async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsPremium(res.data.is_premium || false);
    } catch (error) {
      console.error("Error fetching premium status:", error);
    }
  };

  // Specialised meditation audio controls
  const SPECIALISED_MEDITATIONS = [
    { id: "feet_first", title: "Feet First", url: "https://customer-assets.emergentagent.com/job_1a620098-a18c-466c-b17a-d52b9954c838/artifacts/9rzh8bid_FEET%20FIRST.mp3", free: true },
    { id: "circuit_breaker", title: "Circuit Breaker", url: "https://customer-assets.emergentagent.com/job_1a620098-a18c-466c-b17a-d52b9954c838/artifacts/68wcex42_CIRCUIT%20BREAKER.mp3", free: true },
    { id: "the_2am_anchor", title: "The 2AM Anchor", url: "https://customer-assets.emergentagent.com/job_1a620098-a18c-466c-b17a-d52b9954c838/artifacts/5ae455kx_THE%202AM%20ANCHOR.mp3", free: false },
    { id: "soft_landing", title: "Soft Landing", url: "https://customer-assets.emergentagent.com/job_1a620098-a18c-466c-b17a-d52b9954c838/artifacts/a6vitfs7_SOFT%20LANDING.mp3", free: false },
    { id: "in_the_room", title: "In The Room", url: "https://customer-assets.emergentagent.com/job_1a620098-a18c-466c-b17a-d52b9954c838/artifacts/7xxd7ti4_IN%20THE%20ROOM.mp3", free: false },
    { id: "ghost_protocol", title: "Ghost Protocol", url: "https://customer-assets.emergentagent.com/job_1a620098-a18c-466c-b17a-d52b9954c838/artifacts/rh19uulh_GHOST%20PROTOCOL.mp3", free: false },
    { id: "fraud_watch", title: "Fraud Watch", url: "https://customer-assets.emergentagent.com/job_1a620098-a18c-466c-b17a-d52b9954c838/artifacts/45nzcfb7_FRAUD%20WATCH.mp3", free: false },
  ];

  const handlePlaySpecialised = (meditation) => {
    if (!meditation.free && !isPremium) {
      toast.error("Premium subscription required for this meditation");
      navigate("/subscription");
      return;
    }

    if (specialPlaying === meditation.id) {
      // Pause
      if (specialAudioRef.current) {
        specialAudioRef.current.pause();
      }
      setSpecialPlaying(null);
    } else {
      // Play new
      if (specialAudioRef.current) {
        specialAudioRef.current.pause();
      }
      const audio = new Audio(meditation.url);
      audio.play();
      audio.onended = () => setSpecialPlaying(null);
      specialAudioRef.current = audio;
      setSpecialPlaying(meditation.id);
    }
  };

  const toggleFavorite = async (meditationId, e) => {
    e?.stopPropagation();
    try {
      const res = await axios.post(`${API}/favorites/toggle`, {
        item_id: meditationId,
        item_type: 'meditation'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.is_favorited) {
        setFavorites([...favorites, meditationId]);
        toast.success("Added to favorites");
      } else {
        setFavorites(favorites.filter(id => id !== meditationId));
        toast.success("Removed from favorites");
      }
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  const fetchMeditations = async () => {
    try {
      const [meditationsRes, dailyRes] = await Promise.all([
        axios.get(`${API}/content/meditations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/content/daily`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setMeditations(meditationsRes.data.meditations);
      setDailyMeditation(dailyRes.data.meditation);
    } catch (error) {
      console.error("Error fetching meditations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMeditation = (meditation) => {
    if (meditation.is_premium && !isPremium) {
      toast.error("This meditation requires a Premium subscription");
      navigate("/subscription");
      return;
    }
    if (meditation.is_drip_locked) {
      toast("This meditation unlocks next week! Keep coming back.", { icon: "🌱" });
      return;
    }
    setSelectedMeditation(meditation);
    setCurrentStep(0);
    currentStepRef.current = 0;
    setIsPlaying(false);
    setIsAudioGuide(false);
    setIsAudioPlaying(false);
    setIsPaused(false);
  };

  // Calculate pause duration based on step content
  const calculatePauseDuration = (stepText) => {
    // Base pause + additional time for longer steps
    const wordCount = stepText.split(' ').length;
    const basePause = 2000; // 2 seconds base
    const additionalPause = Math.min(wordCount * 100, 5000); // up to 5 more seconds
    
    // Extra pause for steps mentioning "breathe", "pause", "moment", "notice"
    const mindfulWords = ['breathe', 'pause', 'moment', 'notice', 'feel', 'allow', 'rest'];
    const hasMindfulWord = mindfulWords.some(word => stepText.toLowerCase().includes(word));
    const mindfulBonus = hasMindfulWord ? 3000 : 0;
    
    return basePause + additionalPause + mindfulBonus;
  };

  // Play audio for a specific step
  const playStepAudio = async (stepIndex) => {
    if (!selectedMeditation?.steps?.[stepIndex]) return;
    
    const stepText = selectedMeditation.steps[stepIndex];
    setIsAudioLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/meditation/tts`,
        { text: stepText },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Stop any existing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.onended = async () => {
        URL.revokeObjectURL(audioUrl);
        
        // Wait based on content before moving to next step
        const pauseDuration = calculatePauseDuration(stepText);
        
        await new Promise(resolve => setTimeout(resolve, pauseDuration));
        
        // Check if still in audio guide mode and not paused
        if (isAudioGuide && !isPaused) {
          const nextStep = currentStepRef.current + 1;
          if (nextStep < selectedMeditation.steps.length) {
            setCurrentStep(nextStep);
            currentStepRef.current = nextStep;
            playStepAudio(nextStep);
          } else {
            // Meditation complete - record and grow garden
            setIsAudioPlaying(false);
            setIsAudioGuide(false);
            try {
              await axios.post(`${API}/guided-meditations/${selectedMeditation.id}/complete`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              });
              toast.success("Meditation complete! Your garden is growing 🌱");
            } catch (e) {
              toast.success("Meditation complete. Welcome back.");
            }
          }
        }
      };
      
      audio.onerror = () => {
        toast.error("Failed to play audio");
        setIsAudioPlaying(false);
        setIsAudioLoading(false);
      };
      
      await audio.play();
      setIsAudioPlaying(true);
      setIsAudioLoading(false);
      
    } catch (error) {
      console.error("TTS error:", error);
      toast.error("Unable to generate audio. Please try again.");
      setIsAudioLoading(false);
    }
  };

  // Start audio-guided meditation
  const handleStartAudioGuide = () => {
    setIsPlaying(true);
    setIsAudioGuide(true);
    setCurrentStep(0);
    currentStepRef.current = 0;
    setIsPaused(false);
    playStepAudio(0);
  };

  // Start manual (read-along) meditation
  const handleStartManual = () => {
    setIsPlaying(true);
    setIsAudioGuide(false);
    setCurrentStep(0);
    currentStepRef.current = 0;
  };

  // Pause audio guide
  const handlePauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPaused(true);
    setIsAudioPlaying(false);
  };

  // Resume audio guide
  const handleResumeAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsAudioPlaying(true);
    } else {
      playStepAudio(currentStepRef.current);
    }
    setIsPaused(false);
  };

  // Skip to next step
  const handleSkipNext = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const nextStep = currentStepRef.current + 1;
    if (nextStep < selectedMeditation.steps.length) {
      setCurrentStep(nextStep);
      currentStepRef.current = nextStep;
      if (isAudioGuide && !isPaused) {
        playStepAudio(nextStep);
      }
    }
  };

  // Skip to previous step
  const handleSkipPrev = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const prevStep = currentStepRef.current - 1;
    if (prevStep >= 0) {
      setCurrentStep(prevStep);
      currentStepRef.current = prevStep;
      if (isAudioGuide && !isPaused) {
        playStepAudio(prevStep);
      }
    }
  };

  // Stop and exit meditation
  const handleStopMeditation = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
    setIsAudioGuide(false);
    setIsAudioPlaying(false);
    setIsPaused(false);
  };

  const handleNextStep = () => {
    if (currentStep < selectedMeditation.steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      currentStepRef.current = nextStep;
    } else {
      setIsPlaying(false);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      currentStepRef.current = prevStep;
    }
  };

  // Group meditations by focus
  const focusGroups = meditations.reduce((acc, med) => {
    const focus = med.focus || "General";
    if (!acc[focus]) acc[focus] = [];
    acc[focus].push(med);
    return acc;
  }, {});

  const getBackLabel = () => {
    if (isPlaying) return "Stop";
    return selectedMeditation ? "All Meditations" : "Dashboard";
  };

  const getStepDotClass = (index) => {
    if (index === currentStep) return "bg-primary w-6";
    return index < currentStep ? "bg-primary/50" : "bg-muted";
  };

  const specialIconClass = (isCurrentlyPlaying, isLocked) => {
    if (isCurrentlyPlaying) return "bg-teal-500 text-white";
    return isLocked ? "bg-muted" : "bg-teal-500/10";
  };

  const renderSpecialIcon = (isCurrentlyPlaying, isLocked) => {
    if (isLocked) return <Crown className="w-5 h-5 text-muted-foreground" />;
    if (isCurrentlyPlaying) return <Pause className="w-5 h-5" />;
    return <Play className="w-5 h-5 text-teal-600" />;
  };

  const renderSpecialStatus = (isCurrentlyPlaying, isLocked, med) => {
    if (isCurrentlyPlaying) return <span className="text-xs text-teal-600 font-medium">Now playing...</span>;
    if (med.free) return <span className="text-xs text-teal-600 font-medium">Free</span>;
    if (isLocked) return <span className="text-xs text-amber-600 font-medium">Premium</span>;
    return null;
  };

  const renderMeditationIcon = (meditation) => {
    if (meditation.is_premium && !isPremium) return <Crown className="w-6 h-6 text-amber-500" strokeWidth={1.5} />;
    if (meditation.is_drip_locked) return <Lock className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />;
    return <Moon className="w-6 h-6 text-primary" strokeWidth={1.5} />;
  };

  const renderAudioStatus = () => {
    if (isAudioLoading) return <><Loader2 className="w-4 h-4 animate-spin" />Preparing audio...</>;
    if (isAudioPlaying) return <><Volume2 className="w-4 h-4 animate-pulse" />Audio Guide Active</>;
    if (isPaused) return <><Pause className="w-4 h-4" />Paused</>;
    return <><Volume2 className="w-4 h-4" />Audio Guide</>;
  };

  if (loading) {
    return (
      <div className="dark min-h-screen bg-stone-950 flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <Moon className="w-8 h-8 text-teal-400" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dark min-h-screen bg-stone-950 pb-20" data-testid="meditations-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => {
              if (isPlaying) {
                handleStopMeditation();
              } else if (selectedMeditation) {
                setSelectedMeditation(null);
              } else {
                navigate("/dashboard");
              }
            }}
            className="text-muted-foreground hover:text-foreground"
            data-testid="meditations-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {isPlaying ? "Stop" : selectedMeditation ? "All Meditations" : "Dashboard"}
          </Button>
          <div className="flex items-center gap-2">
            <Moon className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Meditations</span>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* List View */}
        {!selectedMeditation && (
          <div className="animate-fade-in">
            <div className="text-center mb-10">
              <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-3">
                52 Guided Meditations
              </h1>
              <p className="text-muted-foreground text-lg max-w-xl mx-auto">
                A year's worth of practices to reconnect you with your inner peace
              </p>
            </div>

            {/* Today's Meditation */}
            {dailyMeditation && (
              <Card 
                className="glass-card rounded-3xl border-0 mb-10 cursor-pointer card-hover"
                onClick={() => handleSelectMeditation(dailyMeditation)}
                data-testid="daily-meditation-feature"
              >
                <CardContent className="p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="w-4 h-4 text-primary" />
                    <span className="text-xs text-primary uppercase tracking-wide">Today's Meditation</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                        <Moon className="w-8 h-8 text-primary" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h2 className="font-heading text-xl font-semibold text-foreground">
                          {dailyMeditation.title}
                        </h2>
                        <p className="text-sm text-muted-foreground mb-1">
                          {dailyMeditation.description}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-primary">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {dailyMeditation.duration}
                          </span>
                          <span className="bg-primary/10 px-2 py-0.5 rounded-full">
                            {dailyMeditation.focus}
                          </span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-6 h-6 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* All Meditations by Focus */}
            {/* Specialised Meditations - Audio Section */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500/20 to-amber-500/20 flex items-center justify-center">
                  <Volume2 className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-semibold text-foreground">Specialised Meditations</h2>
                  <p className="text-xs text-muted-foreground">Original audio by app creator JVDH</p>
                </div>
              </div>

              <div className="space-y-3">
                {SPECIALISED_MEDITATIONS.map((med) => {
                  const isLocked = !med.free && !isPremium;
                  const isCurrentlyPlaying = specialPlaying === med.id;

                  return (
                    <Card
                      key={med.id}
                      className={`rounded-2xl border-0 cursor-pointer transition-all ${
                        isCurrentlyPlaying 
                          ? 'ring-2 ring-teal-400 bg-teal-50/50 dark:bg-teal-950/20' 
                          : 'glass-card card-hover'
                      } ${isLocked ? 'opacity-70' : ''}`}
                      onClick={() => handlePlaySpecialised(med)}
                      data-testid={`specialised-${med.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${specialIconClass(isCurrentlyPlaying, isLocked)}`}>
                              {renderSpecialIcon(isCurrentlyPlaying, isLocked)}
                            </div>
                            <div>
                              <h3 className="font-heading text-base font-semibold text-foreground">
                                {med.title}
                              </h3>
                              {renderSpecialStatus(isCurrentlyPlaying, isLocked, med)}
                            </div>
                          </div>
                          {isLocked && (
                            <Crown className="w-5 h-5 text-amber-500" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg font-semibold text-foreground">All Meditations</h2>
              <Button
                variant={showFavoritesOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                className="rounded-full"
              >
                <Heart className={`w-4 h-4 mr-1 ${showFavoritesOnly ? 'fill-current' : ''}`} />
                {showFavoritesOnly ? 'Show All' : `Favorites (${favorites.length})`}
              </Button>
            </div>
            
            {Object.entries(focusGroups).map(([focus, meds]) => {
              const filteredMeds = showFavoritesOnly 
                ? meds.filter(m => favorites.includes(m.id))
                : meds;
              if (filteredMeds.length === 0) return null;
              
              return (
              <div key={focus} className="mb-8">
                <h2 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary"></span>
                  {focus}
                  <span className="text-sm font-normal text-muted-foreground">({filteredMeds.length})</span>
                </h2>
                <div className="grid gap-3">
                  {filteredMeds.map((meditation) => (
                    <Card
                      key={meditation.id}
                      className={`glass-card rounded-2xl border-0 cursor-pointer card-hover ${(meditation.is_premium && !isPremium) || meditation.is_drip_locked ? 'opacity-70' : ''}`}
                      onClick={() => handleSelectMeditation(meditation)}
                      data-testid={`meditation-${meditation.id}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${(meditation.is_premium && !isPremium) || meditation.is_drip_locked ? 'bg-muted' : 'bg-primary/10'}`}>
                              {renderMeditationIcon(meditation)}
                            </div>
                            <div>
                              <h3 className="font-heading text-base font-semibold text-foreground">
                                {meditation.title}
                              </h3>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {meditation.duration}
                                </span>
                                {meditation.is_premium && !isPremium && (
                                  <span className="text-amber-600 font-medium">Premium</span>
                                )}
                                {meditation.is_drip_locked && (
                                  <span className="text-muted-foreground font-medium">Unlocks week {meditation.unlock_week}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => toggleFavorite(meditation.id, e)}
                              className="p-2 rounded-full hover:bg-primary/10 transition-colors"
                              data-testid={`favorite-${meditation.id}`}
                            >
                              <Heart 
                                className={`w-5 h-5 ${favorites.includes(meditation.id) ? 'text-red-500 fill-red-500' : 'text-muted-foreground'}`} 
                              />
                            </button>
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )})}
            
            {showFavoritesOnly && favorites.length === 0 && (
              <Card className="glass-card rounded-2xl border-0">
                <CardContent className="p-8 text-center">
                  <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading text-lg font-semibold text-foreground mb-2">No favorites yet</h3>
                  <p className="text-muted-foreground">Tap the heart icon on any meditation to add it to your favorites.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Meditation Detail View - Choose Mode */}
        {selectedMeditation && !isPlaying && (
          <div className="animate-fade-in">
            <div className="text-center mb-10">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6 animate-breathe">
                <Moon className="w-10 h-10 text-primary" strokeWidth={1.5} />
              </div>
              <h1 className="font-heading text-3xl font-semibold text-foreground mb-3">
                {selectedMeditation.title}
              </h1>
              <p className="text-muted-foreground text-lg">
                {selectedMeditation.description}
              </p>
              <div className="flex items-center justify-center gap-4 mt-4 text-sm">
                <span className="flex items-center gap-1 text-primary">
                  <Clock className="w-4 h-4" />
                  {selectedMeditation.duration}
                </span>
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full">
                  {selectedMeditation.focus}
                </span>
              </div>
            </div>

            <Card className="glass-card rounded-3xl border-0 mb-6">
              <CardContent className="p-8">
                <h2 className="font-heading text-lg font-semibold text-foreground mb-4">
                  Choose Your Experience
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  This meditation has {selectedMeditation.steps?.length || 0} gentle steps. 
                  Choose how you'd like to experience them.
                </p>
                
                {/* Audio Guide Option - Recommended */}
                <div className="space-y-4">
                  <Card className="border-2 border-primary/30 rounded-2xl bg-primary/5">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <Volume2 className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-heading font-semibold text-foreground">Audio Guide</h3>
                            <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">Recommended</span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-4">
                            Close your eyes and let a soothing voice guide you through each step with gentle pauses.
                          </p>
                          <Button
                            onClick={handleStartAudioGuide}
                            className="btn-primary w-full"
                            data-testid="start-audio-guide-btn"
                          >
                            <Volume2 className="w-4 h-4 mr-2" />
                            Start Audio Guide
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Active Meditation View */}
        {selectedMeditation && isPlaying && (
          <div className="animate-fade-in">
            {/* Progress indicator */}
            <div className="flex justify-center gap-2 mb-8">
              {selectedMeditation.steps?.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${getStepDotClass(index)}`}
                />
              ))}
            </div>

            {/* Audio Guide Status */}
            {isAudioGuide && (
              <div className="flex justify-center mb-4">
                <div className="flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm">
                  {renderAudioStatus()}
                </div>
              </div>
            )}

            {/* Step Display */}
            <Card className="glass-card rounded-3xl border-0 mb-8">
              <CardContent className="p-8 md:p-12 text-center min-h-[300px] flex flex-col items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-8 animate-breathe">
                  <Heart className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <p 
                  className="font-heading text-xl md:text-2xl text-foreground leading-relaxed"
                  data-testid="meditation-step"
                >
                  {selectedMeditation.steps?.[currentStep]}
                </p>
                <span className="text-sm text-muted-foreground mt-6">
                  Step {currentStep + 1} of {selectedMeditation.steps?.length || 0}
                </span>
              </CardContent>
            </Card>

            {/* Audio Guide Controls */}
            {isAudioGuide && (
              <div className="flex justify-center items-center gap-4 mb-6">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleSkipPrev}
                  disabled={currentStep === 0 || isAudioLoading}
                  className="w-12 h-12 rounded-full"
                  data-testid="audio-prev-btn"
                >
                  <SkipBack className="w-5 h-5" />
                </Button>
                
                {isPaused || !isAudioPlaying ? (
                  <Button
                    onClick={handleResumeAudio}
                    disabled={isAudioLoading}
                    className="btn-primary w-16 h-16 rounded-full"
                    data-testid="audio-play-btn"
                  >
                    {isAudioLoading ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <Play className="w-6 h-6" />
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handlePauseAudio}
                    className="btn-primary w-16 h-16 rounded-full"
                    data-testid="audio-pause-btn"
                  >
                    <Pause className="w-6 h-6" />
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleSkipNext}
                  disabled={currentStep === (selectedMeditation.steps?.length || 0) - 1 || isAudioLoading}
                  className="w-12 h-12 rounded-full"
                  data-testid="audio-next-btn"
                >
                  <SkipForward className="w-5 h-5" />
                </Button>
              </div>
            )}

            {/* Manual Navigation */}
            {!isAudioGuide && (
              <div className="flex justify-between items-center">
                <Button
                  variant="ghost"
                  onClick={handlePrevStep}
                  disabled={currentStep === 0}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="meditation-prev-btn"
                >
                  Previous
                </Button>
                
                {currentStep === (selectedMeditation.steps?.length || 0) - 1 ? (
                  <Button
                    onClick={async () => {
                      setIsPlaying(false);
                      try {
                        await axios.post(`${API}/guided-meditations/${selectedMeditation.id}/complete`, {}, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success("Meditation complete! Your garden is growing 🌱");
                      } catch (e) {
                        toast.success("Meditation complete. Welcome back.");
                      }
                      setSelectedMeditation(null);
                    }}
                    className="btn-primary"
                    data-testid="meditation-complete-btn"
                  >
                    Complete
                  </Button>
                ) : (
                  <Button
                    onClick={handleNextStep}
                    className="btn-primary"
                    data-testid="meditation-next-btn"
                  >
                    Continue
                  </Button>
                )}
              </div>
            )}

            {/* Stop Button for Audio Guide */}
            {isAudioGuide && (
              <div className="flex justify-center mt-6">
                <Button
                  variant="ghost"
                  onClick={handleStopMeditation}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="stop-meditation-btn"
                >
                  End Meditation
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default MeditationsPage;

